import { AutoComplete, Col, Input, PageHeader, Row, Button } from 'antd';
import React, { ReactElement, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import useFetch from '../hooks/fetcher';
import { InfoCircleTwoTone } from '@ant-design/icons';

const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';

type botanicalIdType = {
    botanical_id: string;
};

type countType = {
    count: string;
};

type botanicalNameType = {
    botanical_name: string;
} & botanicalIdType;

type synonymsType = {
    synonyms: string;
} & botanicalIdType;

type commonNameType = {
    common_name: string;
} & botanicalIdType;

type chinesePharmaceuticalNameType = {
    chinese_pharmaceutical_name: string;
} & botanicalIdType;

type pinyinType = {
    pinyin: string;
} & botanicalIdType;

type fetchResultType = {
    botanical_name: { data: botanicalNameType[] } & countType;
    synonyms: { data: synonymsType[] } & countType;
    common_name: { data: commonNameType[] } & countType;
    chinese_pharmaceutical_name: { data: chinesePharmaceuticalNameType[] } & countType;
    pinyin: { data: pinyinType[] } & countType;
};

type optionType = {
    label: ReactElement;
    options: {
        value: string;
        label: ReactElement;
    }[];
};

const divider = '%%';

const renderItem = (
    title: string,
    table: string,
    column: string,
    botanicalId: string,
    key: number,
): { value: string; key: string; label: ReactElement } => ({
    value: title,
    key: table + divider + column + divider + title + divider + botanicalId + divider + key,
    label: (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            {title}
            <span>-</span>
        </div>
    ),
});

const updateResultCount = (data: fetchResultType): string =>
    (
        parseInt(data.botanical_name.count) +
        parseInt(data.chinese_pharmaceutical_name.count) +
        parseInt(data.common_name.count) +
        parseInt(data.pinyin.count) +
        parseInt(data.synonyms.count)
    ).toString();

const PageTarget: React.FC = () => {
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const [options, setOptions] = useState<optionType[]>([]);
    const [resultCount, setresultCount] = useState<string>('');
    const history = useHistory();
    const gutterH = 16;
    const gutterV = 16;
    const colHelperProps = {
        xs: 0,
        sm: 0,
        md: 2,
        lg: 5,
        xl: 7,
    };
    const colContentProps = {
        xs: 24,
        sm: 24,
        md: 20,
        lg: 14,
        xl: 10,
    };

    const renderTitle = (title: string, count: string, table: string, column: string, sname: string): ReactElement => (
        <span>
            {title}
            <Link style={{ float: 'right' }} to={`/plant/list/${column}/${sname}`}>
                show all {count} results
            </Link>
        </span>
    );

    const updateOptions = (data: fetchResultType, keyword: string): optionType[] => {
        return [
            {
                label: renderTitle(
                    'Accepted botanical names',
                    data.botanical_name.count,
                    'tcm_info',
                    'botanical_name',
                    keyword,
                ),
                options: data.botanical_name.data.map((e, i) =>
                    renderItem(e.botanical_name, 'tcm_info', 'botanical_name', e.botanical_id, i),
                ),
            },
            {
                label: renderTitle('Botanical synonyms', data.synonyms.count, 'botanical_synonym', 'synonyms', keyword),
                options: data.synonyms.data.map((e, i) =>
                    renderItem(e.synonyms, 'botanical_synonym', 'synonyms', e.botanical_id, i),
                ),
            },
            {
                label: renderTitle(
                    'Common plant names',
                    data.common_name.count,
                    'botanical_common_name',
                    'common_name',
                    keyword,
                ),
                options: data.common_name.data.map((e, i) =>
                    renderItem(e.common_name, 'botanical_common_name', 'common_name', e.botanical_id, i),
                ),
            },
            {
                label: renderTitle(
                    'Chinese pharmaceutical names',
                    data.chinese_pharmaceutical_name.count,
                    'tcm_info',
                    'chinese_pharmaceutical_name',
                    keyword,
                ),
                options: data.chinese_pharmaceutical_name.data.map((e, i) =>
                    renderItem(
                        e.chinese_pharmaceutical_name,
                        'tcm_info',
                        'chinese_pharmaceutical_name',

                        e.botanical_id,
                        i,
                    ),
                ),
            },
            {
                label: renderTitle('Pinyin', data.pinyin.count, 'tcm_info', 'pinyin', keyword),
                options: data.pinyin.data.map((e, i) => renderItem(e.pinyin, 'tcm_info', 'pinyin', e.botanical_id, i)),
            },
        ];
    };

    // choose one of the results
    const onSelect = (e: string, opt: any): void => {
        const [table, column, keyword, botanicalId] = opt.key.split(divider);
        history.push(`/plant/network/${table}/${column}/${keyword}/${botanicalId}`);
    };

    // onChange event
    const onSearch = (e: string): Promise<void> =>
        get('/api/plant_idx.php', { name: e }).then((r: fetchResultType) => {
            setOptions(updateOptions(r, e));
            const c = updateResultCount(r);
            setresultCount(e === '' ? '' : c + ' results. Press enter to show them all.');
        });

    // go to the search in all categories Page
    const onTriggerSelect = (keyword: string): void => {
        history.push(`/plant/list/all/${keyword}`);
    };

    return (
        <div>
            <PageHeader title="Plant / Herb search" style={{ marginBottom: '10vh' }} />
            <Row justify="space-between" gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps} style={{ display: 'flex' }}>
                    <AutoComplete style={{ flex: 1 }} options={options} onSelect={onSelect} onSearch={onSearch}>
                        <Input.Search size="large" placeholder="Search plant / herb" onSearch={onTriggerSelect} />
                    </AutoComplete>
                </Col>
                <Col {...colHelperProps}>{resultCount}</Col>
            </Row>
            <Row gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps}>
                    <Button type="link" onClick={() => history.push('/animal/0')}>
                        -&gt; Animal/Mineral Products List
                    </Button>
                    {/* <strong style={{ color: 'blue' }}>-&gt; Animal/Mineral Products List</strong> */}
                </Col>
                <Col {...colHelperProps}></Col>
            </Row>
            <Row gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps}>
                    <strong>Examples:</strong>
                    <br />
                    Accepted botanical name:<strong> Rubus idaeus L.</strong>
                    <br />
                    Botanical synonyms:
                    <strong> Rubus idaeus, Rubus idaeus Linné and Rubus idaeus L. subsp. idaeus</strong>
                    <br />
                    Common plant name:<strong> raspberry</strong>
                    <br />
                    Chinese pharmaceutical name:<strong> 覆盆子</strong>
                    <br />
                    Transliteration of Chinese (Pinyin):<strong> Fu Pen Zi</strong>
                </Col>
                <Col {...colHelperProps}></Col>
            </Row>

            <Row gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps}>
                    <div>
                        <strong>Info:</strong>
                        <br />
                        An <strong>&quot;accepted botanical name&quot;</strong> is the scientific unique name of a plant
                        species, e.g. Rubus idaeus L..
                    </div>
                    <div>
                        A <strong>&quot;botanical synonym&quot;</strong>
                        is a scientific botanical synonyms, e.g. Rubus idaeus, Rubus idaeus L. subsp. idaeus and Rubus
                        idaeus L. are the same plant. We take Rubus idaeus L. as this plant&apos;s correct accepted
                        scientific name, while the other ones are its synonyms.
                    </div>
                    <div>
                        <strong>Common plant name:</strong> (in various languages) Raspberry, Himbeere etc.
                    </div>
                    <div>
                        <strong>Chinese pharmaceutical name:</strong> 覆盆子
                    </div>
                    <div>
                        <strong>Transliteration :</strong> is the phonology of the Chinese pharmaceutical name, e.g. Fu
                        Pen Zi is the transliteration of the above 覆盆子
                    </div>
                    <div>
                        <InfoCircleTwoTone style={{ marginRight: '5px' }} />
                        In addition to plant/herb search, the search for popular animal and mineral drugs, and for
                        fungals and their sources is possible, too.
                    </div>
                </Col>
                <Col {...colHelperProps}></Col>
            </Row>
        </div>
    );
};

export default PageTarget;
